/**
 * WordPress dependencies
 */
import { serialize } from '@safe-wordpress/blocks';
import { useSelect, useDispatch } from '@safe-wordpress/data';
import { store as CORE } from '@safe-wordpress/core-data';
import { useCallback } from '@safe-wordpress/element';

/**
 * Internal dependencies
 */
import { DEFAULT_FORM_CONTENT } from './defaults';
import type { Form } from './types';

export const useRefStatus = (
	clientId: string,
	ref?: number
): {
	readonly hasResolved: boolean;
	readonly isMissing: boolean;
} =>
	useSelect(
		( select ) => {
			const persistedBlockFull = select( CORE ).getEntityRecord(
				'postType',
				'nelio_form',
				ref
			);
			const persistedBlockReadOnly = select( CORE ).getEntityRecord(
				'postType',
				'nelio_form',
				ref,
				{
					context: 'view',
				}
			);
			const hasResolvedBlock =
				!! select( CORE ).hasFinishedResolution( 'getEntityRecord', [
					'postType',
					'nelio_form',
					ref,
				] ) &&
				!! select( CORE ).hasFinishedResolution( 'getEntityRecord', [
					'postType',
					'nelio_form',
					ref,
					{ context: 'view' },
				] );
			return {
				hasResolved: hasResolvedBlock,
				isMissing:
					hasResolvedBlock &&
					! persistedBlockFull &&
					! persistedBlockReadOnly,
			};
		},
		[ ref, clientId ]
	);

export const useRenderedFormContent = ( ref: number ): string =>
	useSelect(
		( select ) => {
			const persistedBlockReadOnly = select( CORE ).getEntityRecord(
				'postType',
				'nelio_form',
				ref,
				{
					context: 'view',
				}
			);
			return (
				(
					persistedBlockReadOnly as {
						content?: { rendered?: string };
					}
				 ).content?.rendered ?? ''
			);
		},
		[ ref ]
	);

export const useForms = (): {
	readonly forms: ReadonlyArray< Form >;
	readonly hasResolved: boolean;
} =>
	useSelect( ( select ) => {
		const forms: ReadonlyArray< Record< string, unknown > > =
			select( CORE ).getEntityRecords( 'postType', 'nelio_form' ) ?? [];
		const hasResolvedForms = !! select( CORE ).hasFinishedResolution(
			'getEntityRecords',
			[ 'postType', 'nelio_form' ]
		);
		return {
			hasResolved: hasResolvedForms,
			forms: forms.filter( isForm ),
		};
	} );

const isForm = ( f: Partial< Form > ): f is Form =>
	!! f && !! f.id && !! f.slug && !! f.type && !! f.title?.raw;

export const useCreateForm = (
	title: string,
	setRef: ( ref: number ) => void
): ( () => void ) => {
	const { saveEntityRecord } = useDispatch( CORE );

	const form = {
		title,
		content: serialize( DEFAULT_FORM_CONTENT ),
		status: 'publish',
	};

	return useCallback( async () => {
		const updatedForm = ( await saveEntityRecord(
			'postType',
			'nelio_form',
			form
		) ) as Form;
		setRef( updatedForm.id );
	}, [ title ] );
};
